import { useEffect, useState, useRef, useCallback } from 'react';
import api from '@/lib/api';

/**
 * useActivityCounters — drives the green-dot + count badge on each
 * dashboard tab button.
 *
 *   • Polls /api/dashboard/activity-counters every 30 s.
 *   • `since` = the doctor's most recent LOCAL midnight expressed in UTC.
 *     At local midnight we recompute it → all counts naturally reset to 0.
 *   • `counts[tab]` = total inserts today.
 *   • `unseen[tab]` = inserts since the doctor last clicked this tab
 *     (drives the pulsing dot). Resets to 0 the moment the doctor opens
 *     the tab, but `counts[tab]` keeps showing the full daily total
 *     until midnight rollover — per the user's spec
 *     ("dot goes away but the count stays till midnight").
 */
const STORE = 'docsite_seen_counts_v1';

/* Reflect the number of NEW (unseen) dashboard items onto the installed PWA's
   home-screen app icon (Badging API) — like WhatsApp/Gmail. Works on Android
   installed PWAs and Chrome/Edge desktop; a no-op in plain browser tabs and on
   iOS (iOS badges the icon via the push notification instead). The badge
   persists on the icon after the app is closed until it's cleared. */
function applyAppBadge(countsObj, seen) {
  try {
    if (!('setAppBadge' in navigator)) return;
    const total = Object.keys(countsObj || {}).reduce(
      (sum, k) => sum + Math.max(0, (countsObj[k] || 0) - (seen[k] || 0)),
      0,
    );
    if (total > 0) navigator.setAppBadge(total);
    else if (navigator.clearAppBadge) navigator.clearAppBadge();
  } catch { /* badging unsupported / blocked */ }
}

function localMidnightUtcIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function loadSeen() {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    // Drop the stored map if it was for a different local day so the
    // dot starts fresh every morning (counts hit 0 too because `since`
    // rolls over server-side at the same moment).
    if (obj.date !== new Date().toDateString()) return {};
    return obj.seen || {};
  } catch {
    return {};
  }
}

function saveSeen(seen) {
  try {
    localStorage.setItem(STORE, JSON.stringify({ date: new Date().toDateString(), seen }));
  } catch { /* private mode */ }
}

export default function useActivityCounters(enabled = true) {
  const [counts, setCounts] = useState({});
  const seenRef = useRef(loadSeen());
  const sinceRef = useRef(localMidnightUtcIso());

  const tick = useCallback(async () => {
    // Daily rollover — push `since` forward, drop yesterday's seen map.
    const newSince = localMidnightUtcIso();
    if (newSince !== sinceRef.current) {
      sinceRef.current = newSince;
      seenRef.current = {};
      saveSeen({});
    }
    try {
      const { data } = await api.get('/dashboard/activity-counters', {
        params: { since: sinceRef.current },
      });
      const fresh = data?.counts || {};
      setCounts(fresh);
      applyAppBadge(fresh, seenRef.current);
    } catch { /* network blip — keep last counts visible */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [enabled, tick]);

  /** Doctor opened this tab — dismiss the dot (but the count badge stays). */
  const acknowledge = useCallback((tabKey) => {
    const current = counts[tabKey] || 0;
    seenRef.current = { ...seenRef.current, [tabKey]: current };
    saveSeen(seenRef.current);
    // Re-sync the home-screen icon badge now that this tab is acknowledged.
    applyAppBadge(counts, seenRef.current);
    // Force a render so the dot disappears.
    setCounts((c) => ({ ...c }));
  }, [counts]);

  /** Returns {count, hasUnseen} for a given tab key. */
  const statusFor = useCallback((tabKey) => {
    const count = counts[tabKey] || 0;
    const seen = seenRef.current[tabKey] || 0;
    return { count, hasUnseen: count > seen };
  }, [counts]);

  return { counts, statusFor, acknowledge, refresh: tick };
}
